import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button — shadcn-style primitive bound to Brix brand tokens.
 *
 * Variants:
 * - primary  Brix teal fill, white text. The default action.
 * - secondary White surface, Brix border + ink text.
 * - ghost    Transparent, hover tint. Header/sidebar nav.
 * - subtle   Muted text on transparent. Sign-out, "Manage locations".
 *
 * No raw colors — everything reads from src/styles/globals.css.
 * Cursor-pointer + 200ms transition + active scale baked in per Brix checklist.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg',
    'font-display font-semibold text-sm cursor-pointer',
    'transition-all duration-200 ease-out',
    'active:scale-[0.98]',
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary focus-visible:ring-offset-2',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-brix-primary text-white shadow-card hover:shadow-card-hover hover:bg-brix-primary/95',
        secondary:
          'bg-brix-surface text-brix-ink border border-brix-border hover:bg-brix-primary-tint hover:border-brix-primary/30',
        ghost: 'bg-transparent text-brix-ink hover:bg-brix-primary-tint',
        subtle: 'bg-transparent text-brix-muted hover:bg-brix-primary-tint hover:text-brix-ink',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-sm',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
